<template>
    <div class="xl:w-3/4">
        <div class="pt-2 pb-1 text-2xl font-bold">Items</div>

        <div class="flex pb-1">
            <div class="flex-1">
                <div v-if="isCustomer">
                    <label class="flex items-center">
                        <jet-checkbox :value="form.allow_substitutions" v-model:checked="form.allow_substitutions"/>
                        <span class="ml-2 text-sm font-bold">Allow Substitutions</span>
                    </label>
                    <div class="text-xs pl-6">If an item is unavailable when preparing your order, allow us to substitution some or all of it for a similar item.</div>
                </div>
                <div v-else class="font-bold">
                    Substitutions {{ form.allow_substitutions ? '' : 'Not' }} Allowed
                </div>
            </div>

            <div v-if="!isCustomer" class="flex-0">
                <toggle id="override_stock_restrictions" class="py-1" v-model="form.override_stock_restrictions" :disabled="!canEdit"/>
                <jet-label for="override_stock_restrictions" value="Override Qty on Hand Restrictions" class="ml-2 inline-block" />
            </div>
        </div>

        <div class="rounded-t overflow-hidden border border-b-0">
            <table class="table w-full">
                <thead>
                    <tr>
                        <th v-if="!isCustomer" class="text-left">Item #</th>
                        <th class="text-left w-1/2 md:w-1/3">Product</th>
                        <th class="hidden md:table-cell text-left w-1/3">Notes</th>
                        <th v-if="!isSelectedCustomerInventoryAccount" class="text-center ">Price</th>
                        <th class="text-center ">Quantity</th>
                        <th v-if="!isSelectedCustomerTaxExempt && !isSelectedCustomerInventoryAccount" class="hidden sm:table-cell text-center">Tax</th>
                        <th v-if="!isSelectedCustomerInventoryAccount" class="hidden md:table-cell text-right whitespace-nowrap">Line Total</th>
                        <th></th>
                    </tr>
                    <tr v-if="$page.props.errors.order_items">
                        <td colspan="6" class="bg-white">
                            <jet-input-error :message="$page.props.errors.order_items" class="mt-1" />
                        </td>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(item,index) in form.order_items" :key="index">
                        <td v-if="!isCustomer">
                            {{ item.product_id }}
                        </td>
                        <td>
                            {{ item.title }}
                            <span v-if="item.description"> - {{ item.description }}</span>
                            <span v-if="item.size"> - {{ item.size }}</span>
                            <span v-if="item.product.special_order" class="ml-1 text-xs italic text-red">(Special Order Item)</span>
                        </td>
                        <td class="hidden md:table-cell">
                            <template v-if="isAdmin && canEditItem(item)">
                                <jet-input
                                    type="text"
                                    class="notes text-left w-full"
                                    v-model="item.product_notes"
                                    @keydown.enter.prevent
                                    maxlength="191"
                                    :disabled="!canEdit"
                                />
                                <jet-input-error :message="$page.props.errors[`order_items.${index}.product_notes`]" class="mt-1" />
                            </template>

                            <p v-else class="text-left text-muted">{{ item.product_notes }}</p>
                        </td>
                        <td v-if="!isSelectedCustomerInventoryAccount" class="text-center">
                            <template v-if="isAdmin && canEditItem(item)">
                                <jet-input
                                    type="number"
                                    class="price text-right"
                                    min="0"
                                    step="0.01"
                                    v-model="item.price"
                                    v-numbers
                                    @keydown.enter.prevent
                                    :disabled="!canEdit"
                                />
                                <jet-input-error :message="$page.props.errors[`order_items.${index}.price`]" class="mt-1" />
                            </template>

                            <p v-else class="text-right">{{ toCurrency(item.price) }}</p>
                        </td>
                        <td class="text-center">
                            <div class="relative">
                                <jet-tooltip v-if="stockWarningItemIndex === index">
                                    Only <span v-if="form.override_stock_restrictions">9999</span><span v-else>{{ maxAvailable(item).qty }}</span> {{ maxAvailable(item).type }}
                                </jet-tooltip>

                                <jet-input-quantity
                                    v-model.number="item.quantity"
                                    class="quantity text-center"
                                    :step="item.product.sfd_product || item.partial_qty ? 0.5 : 1"
                                    :min="(order.status == 1 && manualPickingMode) ? 0 : null"
                                    :max="maxAvailable(item).qty"
                                    :overrideMax="form.override_stock_restrictions"
                                    :ref="'qty' + index"
                                    :disabled="!canEdit"
                                    :title="!item.product.enabled ? 'Product quantity cannot be edited at this time. Please contact PE if you have any questions.' : ''"
                                    @input="updateRetailPrice(index); ensureStockAvailable(index)"
                                    @change="updateRetailPrice(index);"
                                    @stockWarning="stockWarning(index)"
                                    @keydown.enter.prevent
                                />

                                <jet-input-error :message="$page.props.errors[`order_items.${index}.quantity`]" class="mt-1" />
                            </div>
                        </td>
                        <td v-if="!isSelectedCustomerTaxExempt && !isSelectedCustomerInventoryAccount" class="hidden sm:table-cell">
                            <img v-if="item.tax" src="/images/icons/check.svg" width="24" height="24" class="mx-auto"/>
                            <img v-else src="/images/icons/x.svg" width="24" height="24" class="mx-auto" />
                        </td>
                        <td v-if="!isSelectedCustomerInventoryAccount" class="hidden md:table-cell text-right">{{ lineTotal(item) }}</td>
                        <td class="text-center" style="width: 56px;">
                            <jet-secondary-button type="button" @click="removeItem(index)" :disabled="!canEdit || !canEditItem(item)">
                                <img src="/images/icons/trash_w.svg" width="16" height="16" alt="Delete" style="min-width: 16px;"/>
                            </jet-secondary-button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div class="flex flex-col md:flex-row justify-between">
            <div v-if="canAddItems" class="flex gap-x-2 pt-2">
                <div v-if="isAdmin">
                    <jet-input-quantity
                        v-model.number="qtyToAdd"
                        class="quantity text-center h-7"
                        placeholder="Qty"
                        :step="0.5"
                        :ref="'qtyToAdd'"
                        @keydown.enter.prevent
                    />
                </div>

                <div class="w-80">
                    <multiselect class="multi-sm h-7 text-base"
                        v-model="itemToAdd"
                        :options="productOptions"
                        :searchable="true"
                        valueProp="id"
                        :trackBy="['id', 'title']"
                        label="title"
                        placeholder="Select Product"
                        @select="addItem"
                        ref="itemMultiselect"
                        :loading="loadingProducts"
                    >
                        <template v-slot:option="{ option }">
                            <span
                                :class="{ 'text-red-500' : (option.special_order === true || option.category_id === this.dairyCategory) && !canAddSpecialOrderItems,
                                          'text-gray-400' : option.enabled === false
                                                            || option.available === 0,
                                        }"
                            >
                                <div v-if="option.available === 0">
                                    <span class="text-xs text-white bg-red-400 rounded py-1 px-2">Out of Stock</span>
                                </div>

                                <span v-if="!isCustomer">{{ option.id }} - </span>{{ option.title }}
                                <span class="text-xs italic text-red" v-if="option.special_order">(Special Order Item)</span>
                            </span>
                        </template
                        >
                    </multiselect>

                    <jet-input-error :message="addItemError" class="mt-1" />

                </div>
            </div>

            <div v-if="!isSelectedCustomerInventoryAccount" class="flex">
                <div v-if="order.is_wholesale && !canPlaceOrder" class="text-red-600 py-1 self-end">
                    Order minimum of {{ toCurrency(parseFloat(customerOrderMinimum)) }} not met
                </div>
                <span v-else-if="order.is_wholesale && !minimumMet" class="text-gray-500 py-1 self-end">
                    A delivery fee of {{ toCurrency(parseFloat(deliveryFeeBelowMinimum)) }} applies to deliveries below {{ toCurrency(parseFloat(customerOrderMinimum)) }}.
                </span>
            </div>

            <div v-if="!isSelectedCustomerInventoryAccount" class="mt-1 self-end md:pr-14">
                <table>
                    <tr>
                        <th class="px-2 py-1 text-right">Subtotal:</th>
                        <td class="px-2 py-1 text-right">{{ toCurrency(subtotal) }}</td>
                    </tr>
                    <tr v-if="!isCustomer && order.status == 0">
                        <th class="px-2 py-1 text-right whitespace-nowrap">Delivery Fee:</th>
                        <td class="px-2 py-1 text-right">
                            <div class="flex justify-end">
                                <div class="py-1 px-3 border border-r-0 rounded-r-none rounded-l">$</div>
                                <jet-input type="text" class="rounded-l-none w-24 text-right" v-model="form.delivery_fee" :disabled="!canEdit"/>
                            </div>
                            <jet-input-error :message="$page.props.errors.delivery_fee" class="mt-1" />
                        </td>
                    </tr>
                    <tr v-else>
                        <th class="px-2 py-1 text-right">Delivery Fee:</th>
                        <td class="px-2 py-1 text-right">{{ toCurrency(delivery_fee) }}</td>
                    </tr>
                    <tr v-if="!isSelectedCustomerTaxExempt">
                        <th class="px-2 py-1 text-right">Tax:</th>
                        <td class="px-2 py-1 text-right">{{ toCurrency(tax) }}</td>
                    </tr>
                    <tr v-if="!isCustomer && promoCodeTotal > 0">
                        <th class="px-2 py-1 text-right">
                            <div>Promo Code:</div>
                            <div class="font-grey-500 font-normal">
                                {{ order.applied_promo_code.code }}
                                <span v-if="order.promo_code_percent">({{ order.promo_code_percent }}%)</span>
                            </div>
                        </th>
                        <td class="px-2 py-1 text-right whitespace-nowrap">- {{ toCurrency(promoCodeTotal) }}</td>
                    </tr>
                    <!-- <tr v-if="(customerHasCreditBalance || order.applied_credit_amount > 0) && isAdmin">
                        <th class="px-2 py-1 text-right whitespace-nowrap">Prepaid:</th>
                        <td class="px-2 py-1 text-right relative">
                            <jet-tooltip v-if="maxCreditExceeded">
                                {{ maxCreditExceededMessage }}
                            </jet-tooltip>

                            <div class="flex justify-end">
                                <div class="py-1 px-3 border border-r-0 rounded-r-none rounded-l">$</div>
                                <jet-input  type="number" min="0" step="0.01" v-numbers class="rounded-l-none w-24 text-right" v-model="form.applied_credit_amount"/>
                            </div>
                        </td>
                        <jet-input-error :message="$page.props.errors.applied_credit_amount" class="mt-1" />
                    </tr>
                    <tr v-else-if="form.applied_credit_amount > 0">
                        <th class="px-2 py-1 text-right">
                            Prepaid:
                        </th>
                        <td class="px-2 py-1 text-right">{{ toCurrency(parseFloat(form.applied_credit_amount)) }}</td>
                    </tr> -->
                    <tr v-if="form.applied_credit_amount > 0">
                        <th class="px-2 py-1 text-right">Prepaid:</th>
                        <td class="px-2 py-1 text-right">{{ toCurrency(parseFloat(form.applied_credit_amount)) }}</td>
                    </tr>
                    <tr>
                        <th class="px-2 py-1 text-right">Total:</th>
                        <td class="px-2 py-1 text-right">{{ toCurrency(totalAfterCredit) }}</td>
                    </tr>
                    <tr v-if="preauthorized !== null" class="pt-4">
                        <th class="px-2 pt-4 pb-1 text-right">Preauthorized:</th>
                        <td
                            class="px-2 pt-4 pb-1 text-right"
                        >
                            {{ toCurrency(preauthorized) }}
                        </td>
                    </tr>
                    <tr v-if="lossOnPreauth !== null && lossOnPreauth > 0">
                        <th class="px-2 py-1 text-right text-red-600">Loss:</th>
                        <td
                            class="px-2 py-1 text-right text-red-600"
                        >
                            {{ toCurrency(lossOnPreauth) }}
                        </td>
                    </tr>
                    <tr v-if="!isCustomer && order.is_wholesale">
                        <th>
                          <jet-label for="is_pricing_locked" value="Lock in pricing:" class="text-sm" />
                        </th>
                        <td>
                          <jet-input
                                type="checkbox"
                                v-model:checked="form.is_pricing_locked"
                                @click="togglePricingLock()"
                                class="h-5 w-5"
                                :disabled="!canEdit"
                          ></jet-input>
                        </td>
                    </tr>
                </table>
            </div>
        </div>

        <jet-confirmation-modal :show="showInactiveModal" @close="showInactiveModal = false">
            <template #title>
                Inactive Item
            </template>

            <template #content>
                Selected item is inactive, do you wish to add it anyway?
            </template>

            <template #footer>
                <jet-danger-button @click="handleAddItem" ref="addItemYes">
                    Yes
                </jet-danger-button>

                <jet-tertiary-button class="ml-2" @click="cancelAddItem">
                    No
                </jet-tertiary-button>
            </template>
        </jet-confirmation-modal>

        <jet-confirmation-modal :show="showUnavailableModal" @close="showUnavailableModal = false">
            <template #title>
                Item Unavailable
            </template>

            <template #content>
                Selected item has no Qty on Hand available, do you wish to add it anyway?
            </template>

            <template #footer>
                <jet-danger-button @click="handleAddItem" ref="addItemYes">
                    Yes
                </jet-danger-button>

                <jet-tertiary-button class="ml-2" @click="cancelAddItem">
                    No
                </jet-tertiary-button>
            </template>
        </jet-confirmation-modal>
    </div>
</template>

<script>
import { nextTick } from 'vue';
import { DateTime } from "luxon";
import { usePage } from '@inertiajs/inertia-vue3'
import UseUserAuth from '@/Composables/UseUserAuth';
import JetConfirmationModal from '@/Jetstream/ConfirmationModal';
import JetDangerButton from '@/Jetstream/DangerButton';
import JetInput from '@/Jetstream/Input';
import JetInputQuantity from '@/Jetstream/InputQuantity';
import JetInputError from '@/Jetstream/InputError';
import JetLabel from '@/Jetstream/Label';
import JetSecondaryButton from '@/Jetstream/SecondaryButton';
import JetTertiaryButton from '@/Jetstream/TertiaryButton';
import JetTooltip from '@/Jetstream/Tooltip';
import JetCheckbox from '@/Jetstream/Checkbox';
import Toggle from '@vueform/toggle';
import Multiselect from '@vueform/multiselect';

import '@vueform/toggle/themes/default.css';
import '@vueform/multiselect/themes/default.css';

export default {
    components: {
        JetConfirmationModal,
        JetDangerButton,
        JetInput,
        JetInputQuantity,
        JetInputError,
        JetLabel,
        JetSecondaryButton,
        JetTertiaryButton,
        JetTooltip,
        JetCheckbox,
        Toggle,
        Multiselect,
    },

    props: ['order', 'form', 'customers', 'categories', 'is_wholesale', 'orderMinimum', 'deliveryFeeBelowMinimum', 'manualPickingMode'],

    setup () {
        const { isAdmin, isCustomer } = UseUserAuth(usePage().props.value.user);

        return { isAdmin, isCustomer };
    },

    data() {
        return {
            loadingProducts: true,
            products: [],

            manualPickingMode: this.manualPickingMode != undefined ? this.manualPickingMode : false,

            qtyToAdd: '',
            itemToAdd: null,
            addItemError: null,
            showInactiveModal: false,
            showUnavailableModal: false,
            stockWarningItemIndex: null,
            stockWarningTimeout: null,

            autoUpdateDeliveryFee: !this.isAdmin,

            updatePriceAxiosCancel: null,
            maxCreditExceeded: false,
            maxCreditExceededMessage: '',
        }
    },

    mounted () {
        this.autoUpdateDeliveryFee = true;

        this.getProducts();
    },

    computed: {
        selectedCustomer () {
            return this.form.customer_id == null ? null : this.customers.find(i => i.id == this.form.customer_id);
        },

        isSelectedCustomerTaxExempt () {
            return (this.selectedCustomer && this.selectedCustomer.tax_exempt) ? true : false;
        },

        isSelectedCustomerInventoryAccount () {
            return (this.selectedCustomer && this.selectedCustomer.id === 2138) ? true : false;
        },

        customerHasCreditBalance () {
            return (this.selectedCustomer && this.selectedCustomer.credit_balance > 0) ? true : false;
        },

        maxCreditAvailable () {
            if (this.selectedCustomer)
            {
                let customer_credit_balance = this.roundDecimals(parseFloat(this.selectedCustomer.credit_balance ?? 0) + parseFloat(this.order.applied_credit_amount ?? 0));
                
                if (this.total <= 0) return {type: 'total', amount: 0, msg: 'Required'};
                else if (customer_credit_balance > this.total)
                    return {type: 'total', amount: parseFloat(this.total), msg: 'Required'};
                else
                    return {type: 'credit_balance', amount: customer_credit_balance, msg: 'Available'};
            }
            return 0;
        },

        subtotal () {
            return this.roundDecimals(this.form.order_items.reduce(function(accumulator, currentValue){
                return accumulator + (currentValue.quantity * currentValue.price);
            }, 0));
        },

        itemTax () {
            if (this.isSelectedCustomerTaxExempt) return 0;

            let taxRatio = 0.13;
            let tax = this.form.order_items.reduce(function(accumulator, currentValue){
                return accumulator + (currentValue.tax === true ?  (currentValue.quantity * currentValue.price * taxRatio) : 0);
            }, 0);

            return tax;
        },

        delivery_fee ()
        {
            return this.form.delivery_fee ? parseFloat(this.form.delivery_fee) : 0;
        },

        deliveryFeeTax () {
            if (this.isSelectedCustomerTaxExempt) return 0;

            let taxRatio = 0.13;
            let tax = this.delivery_fee * taxRatio

            return tax;
        },

        tax () {
            if (this.isSelectedCustomerTaxExempt) return 0;

            let tax = this.roundDecimals(this.itemTax +this.deliveryFeeTax);

            return tax;
        },

        itemTotal () {
            return this.subtotal + this.itemTax;
        },

        total () {
            let total = this.subtotal + this.tax + this.delivery_fee;

            return this.roundDecimals(total);
        },

        promoCodeTotal() {
            let promo_code_total = 0;

            if (this.order.id === null || this.order.id === undefined) return promo_code_total;

            if (this.order.promo_code_percent !== null) {
                promo_code_total = this.roundDecimals(this.total * (this.order.promo_code_percent / 100));
            }
            else promo_code_total = parseFloat(this.order.promo_code_total ?? 0);

            return promo_code_total;
        },

        totalAfterCredit () {
            return this.roundDecimals(this.total - this.form.applied_credit_amount - this.promoCodeTotal);
        },

        calculatedCreditAmount () {
            // if (this.selectedCustomer && this.selectedCustomer.auto_apply_credit)
            // {
            //     let maxCreditAmount = parseFloat(this.maxCreditAvailable.amount);

            //     if (maxCreditAmount > this.total)
            //         return parseFloat(this.total);
            //     else
            //         return maxCreditAmount;
            // }
            return 0;
        },

        preauthorized () {
            if (this.order.moneris_preauth_amount == undefined || this.order.moneris_preauth_amount === null) return null
            return this.roundDecimals(this.order.moneris_preauth_amount);
        },

        lossOnPreauth () {
            if (this.order.moneris_preauth_amount == undefined || this.order.moneris_preauth_amount === null) return null
            return this.roundDecimals(this.totalAfterCredit - this.preauthorized);
        },

        customerOrderMinimum () {
            if (this.selectedCustomer != null)
            {
                if (this.selectedCustomer.delivery_order_minimum !== null) return this.selectedCustomer.delivery_order_minimum;
                else return this.orderMinimum;
            }     
        },

        canPlaceOrder() {
            // Pickup
            if(this.form.shipping_method === 'pickup') return true;
            // Delivery
            else if (this.itemTotal >= this.customerOrderMinimum) return true;
            else if (this.deliveryFeeBelowMinimum !== null) return true;
            else return false;
        },

        minimumMet() {
            if(this.form.shipping_method === 'pickup') return true;
            return this.form.shipping_method === 'delivery' && this.itemTotal >= this.customerOrderMinimum;
        },

        deliveryFee() {
            let delivery_fee = null;
            if (this.form.shipping_method === 'pickup')
                delivery_fee = null;
            else if (this.deliveryFeeBelowMinimum === null || this.minimumMet)
                if (this.selectedCustomer !== null)
                    delivery_fee = this.selectedCustomer.delivery_fee;
            else
                delivery_fee = this.deliveryFeeBelowMinimum;

            if (delivery_fee !== null) delivery_fee = parseFloat(delivery_fee);

            return delivery_fee;
        },

        productOptions () {
            let products = this.products;
            return products.map(p => {
                let title = p.title;

                if (p.description) title += ' - ' + p.description;
                if (p.size) title += ' - ' + p.size;

                return {
                    id: p.id,
                    title: title,
                    special_order: p.special_order,
                    category_id: p.category_id,
                    available: p.available,
                    enabled: p.enabled,
                }
            });
        },

        canAddSpecialOrderItems() {
            if (this.form.override_ship_date) {
                return true;
            }

            const today = DateTime.now().startOf('day');
            const shipDate = DateTime.fromJSDate(this.form.ship_date).startOf('day');
            const weekDay = today.weekday;

            let daysToAdd = 2;
            if(weekDay === 5) daysToAdd = 4;    // Add 4 days for Friday orders, 3 days for Saturday
            else if (weekDay === 6) daysToAdd = 3;
            const minShipDay = today.plus({days: daysToAdd});

            // Allow special order items with next day ship when admin is creating order.
            // Order will get split on back end.
            return shipDate >= minShipDay || (this.isAdmin && this.form.id === null);
        },

        dairyCategory() {
            let category = this.categories.find(c => c.id == 96);

            if(category === undefined) return null;

            return category.id;
        },

        canAddItems () {
            let canAddItems = 0;

            if (this.order.is_wholesale)
            {
                canAddItems = this.canEdit && (this.order.status == 0 || (this.order.status == 1 && !this.manualPickingMode) || (this.order.status == 1 && this.manualPickingMode && this.order.allow_substitutions));
            } else {
                canAddItems = this.canEdit && (this.order.status == 0 || (this.order.status == 1 && this.manualPickingMode && this.order.allow_substitutions));
            }

            return canAddItems;
        },

        canEdit() {
            // Anyone can edit new orders
            if (this.form.id == null) return true;

            // Wholesale
            if (this.is_wholesale)
            {
                // Anyone can edit new orders
                if (this.order.status == 0) return true;
                // Only non-customers can edit when in progress
                else if (!this.isCustomer && this.order.status == 1) return true;
            }
            // Retail
            else
            {
                if (this.order.status == 1 && this.manualPickingMode) return true;
            }

            return false;
        },
    },

    methods: {
        getProducts () {
            this.loadingProducts = true;

            let getProductsRoute = null;
            if (this.isCustomer) getProductsRoute = route('customer_orders.get_products', {customer_order: this.order.id});
            else getProductsRoute = route('orders.get_products', {order: this.order.id});

            axios
                .get(getProductsRoute,
                    {
                    // query URL without using browser cache
                    headers: {
                        'Cache-Control': 'no-cache',
                        'Pragma': 'no-cache',
                        'Expires': '0',
                    },
                })
                .then(response => {
                    this.products = response.data;

                    this.$nextTick(() => {
                        this.loadingProducts = false;
                    });
                });
        },

        maxAvailable (item) {
            if (this.manualPickingMode && !item.substitution)
            {
                let orig_quantity = item.orig_quantity;
                if (orig_quantity === null) return {'type': 'Available', 'qty': 9999};
                // else if (item.product.available < orig_quantity) return {'type': 'Available', 'qty': item.product.available};
                else return {'type': 'Ordered', 'qty': orig_quantity};
            }
            else return {'type': 'Available', 'qty': item.product.available};
        },

        canEditItem (item) {
            return this.order.status == 0 || (this.order.status == 1 && !this.manualPickingMode) || (this.order.status == 1 && this.manualPickingMode && item.substitution);
        },

        removeItem (index) {
            this.form.order_items.splice(index, 1);
        },

        lineTotal (item) {
            return this.toCurrency(this.roundDecimals(item.price * item.quantity));
        },

        togglePricingLock() {
          this.form.is_pricing_locked = !this.form.is_pricing_locked;

          return this.form.is_pricing_locked;
        },

        handleAddItem () {
            let ref = 'qty' + this.form.order_items.length;
            let product = this.products.find(i => i.id == this.itemToAdd);

            this.addItemError = null;
            if(this.showInactiveModal) this.showInactiveModal = false;
            if(this.showUnavailableModal) this.showUnavailableModal = false;

            if((product.special_order || product.category_id === this.dairyCategory) && !this.canAddSpecialOrderItems)
            {
                this.addItemError = 'Shipping date is too soon to add special order or dairy items';
                this.itemToAdd = null;
                this.$refs.itemMultiselect.clear();
                setTimeout(()=> {
                    this.addItemError = null;
                }, 3000);

            } else {
                let pricing_route = null;
                if (this.is_wholesale)
                {
                    pricing_route = this.route('pricing.fetch', {
                            product: product.id,
                            customer: this.form.customer_id,
                        });
                }
                else
                {
                    pricing_route = this.route('pricing.fetch_retail', {
                        product: product.id,
                    });
                }

                axios
                    .get(pricing_route)
                    .then(response => {
                        if (this.isCustomer && (response.data === null || response.data === '')) {
                            const index = this.products.findIndex(p => p.id === product.id);

                            if (index !== -1) {
                                this.products.splice(index, 1);
                            }

                            this.addItemError = 'Item no longer available';
                        } else {
                            if (!product.allow_partial_quantity && this.qtyToAdd % 1 === 0.5) {
                                this.qtyToAdd = Math.ceil(this.qtyToAdd);
                            }

                            if (!this.form.override_stock_restrictions && product.available < this.qtyToAdd)
                            {
                                this.qtyToAdd = product.available;
                                let stock_warning_index = this.form.order_items.length;
                                setTimeout(() => {
                                    this.stockWarning(stock_warning_index)
                                }, 100);
                            }

                            let quantity = this.isAdmin ? this.qtyToAdd : 1;
                            let price = response.data;
                            if (!this.is_wholesale && quantity >= 2 && product.price_2plus)
                            {
                                price = product.price_2plus;
                            }

                            this.form.order_items.push({
                                order_id: this.form.id,
                                product_id: product.id,
                                sfd_product: product.sfd_product,
                                substitution: this.manualPickingMode,
                                title: product.title,
                                description: product.description,
                                size: product.size,
                                tax: product.taxable,
                                partial_qty: product.allow_partial_quantity,
                                cost: product.cost,
                                price: price,
                                quantity: quantity,
                                orig_quantity: null,
                                product: product,
                                zone: product.zone,
                            });

                            nextTick(() => {
                                if(this.isAdmin) {
                                    this.$refs['qtyToAdd'].$el.focus();
                                    this.$refs['qtyToAdd'].$el.select();
                                }
                                else {
                                    this.$refs[ref][0].$el.focus();
                                    this.$refs[ref][0].$el.select();
                                }
                                window.scrollTo(0, document.body.scrollHeight);
                            });

                            this.itemToAdd = null
                            this.qtyToAdd = '';
                        }
                    });
            }
        },

        addItem() {
            let product = this.products.find(i => i.id == this.itemToAdd);
            let active = product.enabled === true;
            let available = product.available > 0;

            if (active && available) this.handleAddItem();
            else if (!active) {
                this.showInactiveModal = true;
                // Need to use setTimeout instead of nextTick here as modal isn't shown yet
                setTimeout(() => {
                    this.$refs.addItemYes.$el.focus();
                }, 100);
            }
            else if (!available) {
                this.showUnavailableModal = true;
                // Need to use setTimeout instead of nextTick here as modal isn't shown yet
                setTimeout(() => {
                    this.$refs.addItemYes.$el.focus();
                }, 100);
            }
        },

        cancelAddItem() {

            this.$refs.itemMultiselect.clear();

            this.itemToAdd = null;
            this.showInactiveModal = false;
            this.showUnavailableModal = false;

            this.$refs.itemMultiselect.$el.focus();
        },

        updateRetailPrice (index) {
            if (this.is_wholesale) return false;

            let order_item = this.form.order_items[index];
            if (order_item.product.price_2plus)
            {
                if (this.updatePriceAxiosCancel !== null) this.updatePriceAxiosCancel.cancel();
                this.updatePriceAxiosCancel = axios.CancelToken.source();

                if (order_item.quantity >= 2)
                {
                    order_item.price = order_item.product.price_2plus;
                }
                else
                {
                    let pricing_route = this.route('pricing.fetch_retail', {
                            product: order_item.product_id,
                        });

                    axios
                        .get(pricing_route,
                            { cancelToken: this.updatePriceAxiosCancel.token })
                        .then(response => {
                            order_item.price = response.data;
                        });
                }
            }
        },

        ensureStockAvailable (index) {
            if (!this.form.override_stock_restrictions)
            {
                let order_item = this.form.order_items[index];

                let total_quantity = 0;
                this.form.order_items.forEach(item => {
                    if (item.product_id == order_item.product_id)
                    {
                        total_quantity += item.quantity;
                    }
                });

                let maxAvailable = this.maxAvailable(order_item);
                if (maxAvailable < total_quantity)
                {
                    order_item.quantity -= (total_quantity - maxAvailable);
                    this.stockWarning(index);
                }
            }
        },

        stockWarning (index) {
            if (this.stockWarningTimeout) clearTimeout(this.stockWarningTimeout);

            this.stockWarningItemIndex = index;

            this.stockWarningTimeout = setTimeout(() => {
                this.stockWarningItemIndex = null;
                clearTimeout(this.stockWarningTimeout);
            }, 3 * 1000);
        },

        updateCreditAmount () {
            this.$nextTick(() => {
                let maxCreditAmount = parseFloat(this.maxCreditAvailable.amount);

                if (parseFloat(this.form.applied_credit_amount) < 0)
                {
                    this.form.applied_credit_amount = 0;
                    this.maxCreditExceededMessage = 'Can\'t Apply a Negative Amount';
                    this.maxCreditExceeded = true;
                    setTimeout(() => {
                        this.maxCreditExceededMessage = '';
                        this.maxCreditExceeded = false;
                    }, 5000); // Hide the error message after 5 seconds
                }
                else if (parseFloat(this.form.applied_credit_amount) > maxCreditAmount) {
                    this.form.applied_credit_amount = maxCreditAmount;
                    this.maxCreditExceededMessage = 'Only ' + maxCreditAmount + ' ' + this.maxCreditAvailable.msg;
                    this.maxCreditExceeded = true;
                    setTimeout(() => {
                        this.maxCreditExceededMessage = '';
                        this.maxCreditExceeded = false;
                    }, 5000); // Hide the error message after 5 seconds
                }
            });
        },
    },

    watch: {
        canPlaceOrder:{
            handler () {
                this.$emit('updateCanPlaceOrder', this.canPlaceOrder);
            },
            immediate: true,
        },

        deliveryFee:{
            handler () {
                if (this.autoUpdateDeliveryFee)
                    this.form.delivery_fee = this.deliveryFee;
            },
            immediate: true,
        },

        total() {
            this.updateCreditAmount();
        },

        'form.applied_credit_amount' () {
            this.updateCreditAmount();
        },

        calculatedCreditAmount: {
            handler () {
                // if (this.selectedCustomer && this.selectedCustomer.auto_apply_credit)
                // {
                //     this.form.applied_credit_amount = this.calculatedCreditAmount;
                // }
            },
            immediate: true
        }
    }
}
</script>

<style>

</style>
